class popupHelper {

    constructor() {
        this._galleryInstance = null;
        document.addEventListener('DOMContentLoaded', () => this.init());
    }

    async init() {
        let activePopups;
        try {
            const response = await fetch('/Popups/active');
            const data = await response.json();
            if (data.status !== 'success' || !Array.isArray(data.popups)) return;
            activePopups = data.popups;
        } catch (e) {
            return;
        }

        if (activePopups.length === 0) return;

        this._buildActivePopupsList(activePopups);
        document.getElementById('navbar-active-popups-btn')?.classList.remove('d-none');

        const countEl = document.getElementById('navbar-active-popups-count');
        if (countEl) {
            countEl.textContent = activePopups.length;
            countEl.classList.remove('hidden');
        }

        // Auto-show only the single newest active popup (activePopups[0], same
        // ordering /Popups/current used to apply) - same as before, just now
        // sourced from the shared /Popups/active fetch instead of a separate call.
        const popup = activePopups[0];

        const dismissed = this._getDismissedPopups();
        if (dismissed.includes(popup.id)) return;

        // One-shot suppression for the page the action button navigates to:
        // consumed (removed) on read regardless of outcome, so it only ever
        // hides the popup on that single next page load, not permanently and
        // not on any page after that one.
        const suppressedPopupId = sessionStorage.getItem('popup_suppress_next');
        sessionStorage.removeItem('popup_suppress_next');
        if (suppressedPopupId !== null && String(suppressedPopupId) === String(popup.id)) return;

        this._showPopup(popup);
    }

    // Builds the navbar's "active popups" list modal so a user who already
    // dismissed the auto-shown one with "Niet meer tonen" - which only
    // suppresses that automatic pop, not the popup itself - can still find
    // and reopen it (or any other currently active one) on demand.
    _buildActivePopupsList(activePopups) {
        const listEl = document.getElementById('active-popups-list');
        listEl.innerHTML = '';

        const listModalEl = document.getElementById('active-popups-modal');

        activePopups.forEach((popup) => {
            const thumbnail = popup.image_url
                ? '<img src="' + this._escapeHtml(popup.image_url) + '" class="rounded w-100 h-100" style="object-fit:cover;" alt="">'
                : '<div class="w-100 h-100 rounded bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center"><i class="ai-speaker fs-2 text-danger"></i></div>';

            const item = document.createElement('button');
            item.type = 'button';
            item.className = 'list-group-item list-group-item-action d-flex align-items-center';
            item.innerHTML =
                '<div class="flex-shrink-0 me-3" style="width:64px;height:64px;">' + thumbnail + '</div>' +
                '<div class="flex-grow-1 text-start overflow-hidden">' +
                    '<div class="h6 mb-1 text-truncate">' + this._escapeHtml(popup.title ?? '') + '</div>' +
                    '<div class="fs-sm text-muted text-truncate">' + this._escapeHtml(popup.description ?? '') + '</div>' +
                '</div>' +
                '<i class="ai-chevron-right fs-lg text-muted ms-2 flex-shrink-0"></i>';

            item.addEventListener('click', () => {
                // Bootstrap doesn't stack modals cleanly out of the box - close
                // this list first and only open the chosen popup once that
                // close has actually finished, rather than showing both at once.
                listModalEl.addEventListener('hidden.bs.modal', () => {
                    this._showPopup(popup);
                }, { once: true });
                bootstrap.Modal.getOrCreateInstance(listModalEl).hide();
            });

            listEl.appendChild(item);
        });
    }

    _showPopup(popup) {
        document.getElementById('site-popup-modal-title').textContent       = popup.title ?? '';
        document.getElementById('site-popup-modal-description').textContent = popup.description ?? '';

        const modalEl = document.getElementById('site-popup-modal');

        const galleryEl = document.getElementById('site-popup-modal-gallery');
        const imgLinkEl = document.getElementById('site-popup-modal-image-link');
        const imgEl     = document.getElementById('site-popup-modal-image');

        // A popup reopened from the navbar list may not be the one lightGallery
        // was last initialized for (or may have no image at all) - drop any
        // previous instance before (maybe) creating a new one, rather than
        // stacking initializations on the same DOM element.
        if (this._galleryInstance) {
            this._galleryInstance.destroy();
            this._galleryInstance = null;
        }

        if (popup.image_url) {
            imgEl.src      = popup.image_url;
            imgEl.alt      = popup.title ?? '';
            imgLinkEl.href = popup.image_url;
            galleryEl.classList.remove('d-none');
            this._galleryInstance = lightGallery(galleryEl, { speed: 400 });

            // lightGallery appends its lightbox to document.body by default -
            // a sibling of this modal, not a descendant. Bootstrap's modal
            // focus trap listens on document for focusin and yanks focus back
            // inside the modal the instant it lands anywhere else, which is
            // exactly what lightGallery does when it opens. That bounces focus
            // straight back into the modal, so the next Escape press bubbles
            // through the modal's own keydown-dismiss handler *and* triggers
            // lightGallery's own (window-level) Escape handler - closing both.
            // Suspending the focus trap and keyboard-dismiss for as long as
            // the lightbox is open lets lightGallery keep its own focus and
            // handle Escape by itself; both are restored once it closes.
            const modalInstance = bootstrap.Modal.getOrCreateInstance(modalEl);
            galleryEl.addEventListener('lgAfterOpen', () => {
                modalInstance._focustrap.deactivate();
                modalInstance._config.keyboard = false;
            });
            galleryEl.addEventListener('lgBeforeClose', () => {
                modalInstance._config.keyboard = true;
                modalInstance._focustrap.activate();
            });
        } else {
            galleryEl.classList.add('d-none');
        }

        const btnEl = document.getElementById('site-popup-button');
        if (popup.button_text && popup.button_url) {
            btnEl.textContent = popup.button_text;
            btnEl.href        = popup.button_url;
            btnEl.classList.remove('d-none');
        } else {
            btnEl.classList.add('d-none');
        }

        // "Niet meer tonen" used to be a separate checkbox users had to tick
        // *and then* still press "Sluiten" - not obvious it did anything on
        // its own. It's now its own dismiss button; this flag just records
        // which of the two closed the modal so hidden.bs.modal below knows
        // whether to persist the dismissal.
        let doNotShowAgainClicked = false;
        document.getElementById('site-popup-do-not-show-again').addEventListener('click', () => {
            doNotShowAgainClicked = true;
        }, { once: true });

        modalEl.addEventListener('hidden.bs.modal', () => {
            if (doNotShowAgainClicked) {
                this._addDismissedPopup(popup.id);
            }
        }, { once: true });

        // The action button now navigates in the same tab, so the browser
        // starts leaving the page right after this click handler returns -
        // there's no time left for hide()'s fade-out transition to finish and
        // fire hidden.bs.modal (that only worked while the button opened a
        // new tab and left this one idling). Setting this flag (read once,
        // above) means the popup won't immediately re-show on the page this
        // navigates to, without permanently dismissing it like "Niet meer
        // tonen" does - a normal visit to any other page still shows it.
        btnEl.addEventListener('click', () => {
            sessionStorage.setItem('popup_suppress_next', popup.id);
        }, { once: true });

        bootstrap.Modal.getOrCreateInstance(modalEl).show();
    }

    _getDismissedPopups() {
        try {
            return JSON.parse(localStorage.getItem('dismissed_popups') ?? '[]');
        } catch (e) {
            return [];
        }
    }

    _addDismissedPopup(id) {
        const list = this._getDismissedPopups();
        if (!list.includes(id)) {
            list.push(id);
            localStorage.setItem('dismissed_popups', JSON.stringify(list));
        }
    }

    // Popup title/description/image_url are admin-entered but rendered here
    // via innerHTML for the list layout - escape before interpolating so
    // stored content can't inject markup into the page.
    _escapeHtml(value) {
        const div = document.createElement('div');
        div.textContent = value;
        return div.innerHTML;
    }
}

export let PopupHelper = new popupHelper();
